/**
 * Contratos da API (validação de entrada), compartilhados por servidor e cliente.
 */
import { z } from 'zod';
import { TASK_STATUSES } from './types';
import { mapDataSchema } from './orgmap/parse';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const hhmm = z.string().regex(/^\d{2}:\d{2}$/, 'Horário inválido');
const id = z.string().min(1).max(64);
const color = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
const priority = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
const status = z.enum(TASK_STATUSES);

/* ---------- Pessoas ---------- */
export const profileInput = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  color: color.nullable().optional(),
  role: z.enum(['admin', 'editor', 'viewer']).optional(),
  active: z.boolean().optional(),
});

/* ---------- Tarefas ---------- */
export const subtaskInput = z.object({
  id: id,
  text: z.string().trim().min(1).max(300),
  done: z.boolean(),
  assigneeId: id.nullable(),
  due: isoDate.nullable(),
});
export const waitingInput = z.object({ who: z.string().trim().max(60), followUpOn: isoDate.nullable() });
export const classificationInput = z.object({ categoryId: id, subcategoryId: id.nullable() });

export const createTaskInput = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().max(10_000).default(''),
  status: status.default('mapped'),
  projectId: id.nullable().default(null),
  assigneeId: id.nullable().default(null),
  priority: priority.default(0),
  due: isoDate.nullable().default(null),
  themeIds: z.array(id).max(50).default([]),
  entityIds: z.array(id).max(200).default([]),
  classification: classificationInput.nullable().default(null),
  subtasks: z.array(subtaskInput).max(200).default([]),
  waiting: waitingInput.nullable().default(null),
});
export type CreateTaskInput = z.input<typeof createTaskInput>;

export const updateTaskInput = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().max(10_000),
  projectId: id.nullable(),
  assigneeId: id.nullable(),
  priority,
  due: isoDate.nullable(),
  themeIds: z.array(id).max(50),
  entityIds: z.array(id).max(200),
  classification: classificationInput.nullable(),
  subtasks: z.array(subtaskInput).max(200),
  waiting: waitingInput.nullable(),
}).partial();
export type UpdateTaskInput = z.infer<typeof updateTaskInput>;

export const moveTaskInput = z.object({
  status,
  beforeId: id.nullable().default(null),
  waiting: waitingInput.nullable().optional(),
});
export type MoveTaskInput = z.input<typeof moveTaskInput>;

export const createFromMapNoteInput = z.object({ noteNumber: z.number().int() });

/* ---------- Configuração da área ---------- */
export const settingsInput = z.object({
  wipLimit: z.number().int().min(1).max(50),
  projects: z.array(z.object({ id, name: z.string().trim().min(1).max(40), color })).max(100),
  themes: z.array(z.object({ id, name: z.string().trim().min(1).max(40), color })).max(100),
  categories: z.array(z.object({
    id,
    name: z.string().trim().min(1).max(40),
    subcategories: z.array(z.object({ id, name: z.string().trim().min(1).max(40) })).max(50),
  })).max(50),
}).partial();
export type SettingsInput = z.infer<typeof settingsInput>;

/* ---------- Rotinas ---------- */
export const frequencyInput = z.discriminatedUnion('type', [
  z.object({ type: z.literal('daily'), interval: z.number().int().min(1).max(365), businessDaysOnly: z.boolean() }),
  z.object({ type: z.literal('weekly'), interval: z.number().int().min(1).max(52), weekdays: z.array(z.number().int().min(0).max(6)).min(1, 'Escolha ao menos um dia da semana.') }),
  z.object({ type: z.literal('monthly'), interval: z.number().int().min(1).max(24), dayOfMonth: z.union([z.number().int().min(1).max(31), z.literal('last')]) }),
  z.object({ type: z.literal('yearly'), interval: z.number().int().min(1).max(10) }),
]);
export const routineInput = z.object({
  title: z.string().trim().min(1).max(140),
  description: z.string().max(10_000).default(''),
  projectId: id.nullable(),
  assigneeId: id.nullable(),
  priority,
  themeIds: z.array(id).max(50),
  entityIds: z.array(id).max(200),
  classification: classificationInput.nullable(),
  startStatus: z.enum(['mapped', 'todo', 'doing']),
  frequency: frequencyInput,
  startsOn: isoDate,
  leadDays: z.number().int().min(0).max(60),
  checklist: z.array(z.string().trim().min(1).max(300)).max(50),
  active: z.boolean().default(true),
});
export type RoutineInput = z.input<typeof routineInput>;

/* ---------- Agenda ---------- */
export const eventInput = z.object({
  title: z.string().trim().min(1).max(120),
  location: z.string().trim().max(200).default(''),
  date: isoDate,
  start: hhmm,
  end: hhmm,
  calendar: z.enum(['team', 'meetings', 'clients']),
}).refine(e => e.end > e.start, { message: 'O término precisa ser depois do início.', path: ['end'] });
export type EventInput = z.input<typeof eventInput>;

/* ---------- Notas ---------- */
export const noteInput = z.object({
  title: z.string().max(120),
  body: z.string().max(100_000),
  pinned: z.boolean(),
}).partial();
export type NoteInput = z.infer<typeof noteInput>;

/* ---------- Mapa societário ---------- */
export const importMapInput = z.object({ fileName: z.string().max(200), data: mapDataSchema });

export { isoDate as isoDateSchema };
